/**
 * analytics.js — thin wrapper around Google Analytics 4 (gtag).
 *
 * Why this exists:
 *   - We want to fire standard ecommerce events (view_item, add_to_cart,
 *     begin_checkout, purchase) without sprinkling gtag('event', ...) calls
 *     across components.
 *   - We want analytics to silently no-op when no GA4 measurement ID is
 *     configured, so the site works fine in dev without it.
 *
 * How to enable:
 *   Set VITE_GA4_MEASUREMENT_ID=G-XXXXXXXXXX in frontend-dtc/.env (or .env.production).
 *   On first load, this module injects the gtag.js script and configures the property.
 *
 * Usage:
 *   import analytics from '@/services/analytics'
 *   analytics.init()                                        // call once at app startup
 *   analytics.viewItem(product)
 *   analytics.addToCart(product, quantity)
 *   analytics.beginCheckout(cartItems, totals)
 *   analytics.purchase(order)
 */

const measurementId = import.meta.env.VITE_GA4_MEASUREMENT_ID || null

let initialized = false

function gtag(...args) {
  if (typeof window === 'undefined') return
  // Push to the dataLayer queue (gtag.js initializes from there)
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(args)
}

function init() {
  if (initialized || !measurementId || typeof window === 'undefined') return
  initialized = true

  // Inject the GA4 script tag
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
  document.head.appendChild(script)

  // Initial config — anonymize IP for light privacy hygiene
  gtag('js', new Date())
  gtag('config', measurementId, { anonymize_ip: true })
}

// --- Ecommerce events --------------------------------------------------
// Shapes follow GA4's recommended ecommerce event schema:
//   https://developers.google.com/analytics/devguides/collection/ga4/reference/events

function viewItem(product) {
  if (!product) return
  gtag('event', 'view_item', {
    currency: 'USD',
    value: Number(product.default_price ?? product.price ?? 0),
    items: [productToItem(product)]
  })
}

function addToCart(product, quantity = 1) {
  if (!product) return
  const item = productToItem(product, quantity)
  gtag('event', 'add_to_cart', {
    currency: 'USD',
    value: item.price * item.quantity,
    items: [item]
  })
}

function beginCheckout(cartItems = [], totals = {}) {
  gtag('event', 'begin_checkout', {
    currency: 'USD',
    value: Number(totals.subtotal || 0),
    items: cartItems.map(i => cartItemToItem(i))
  })
}

function purchase(order) {
  if (!order) return
  gtag('event', 'purchase', {
    transaction_id: order.orderNumber || order.id,
    currency: 'USD',
    value: Number(order.total || 0),
    tax: Number(order.taxAmount || 0),
    shipping: Number(order.shippingAmount || 0),
    items: (order.items || []).map(i => orderItemToItem(i))
  })
}

// --- Mappers ----------------------------------------------------------

function productToItem(product, quantity = 1) {
  return {
    item_id: String(product.sku || product.id),
    item_name: product.name,
    price: Number(product.default_price ?? product.price ?? 0),
    quantity,
    item_category: product.category || 'Wine Spritz'
  }
}

function cartItemToItem(cartItem) {
  return {
    item_id: String(cartItem.sku || cartItem.productId),
    item_name: cartItem.name || cartItem.productName,
    price: Number(cartItem.priceAtTime || cartItem.price || 0),
    quantity: cartItem.quantity
  }
}

function orderItemToItem(orderItem) {
  return {
    item_id: String(orderItem.sku || orderItem.productId),
    item_name: orderItem.productName || orderItem.name,
    price: Number(orderItem.priceAtTime || 0),
    quantity: orderItem.quantity
  }
}

export default {
  init,
  viewItem,
  addToCart,
  beginCheckout,
  purchase,
  isEnabled: () => Boolean(measurementId)
}
